package ui.app.assign.detail.wheel.preview

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import api.AppType
import compose.assets.Svg
import compose.assets.svg.Download
import compose.assets.svg.Package_down
import compose.assets.svg.Package_up
import org.jetbrains.compose.resources.StringResource
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.compose.navigation.navTo_AppEditorWheelUI
import tk.mallumo.utils.DataState
import tools.rememberString
import ui.app.assign.detail.AppDeleteDialog
import ui.app.assign.detail.AppDetailArgs
import ui.app.assign.detail.wheel.preview.pages.AppWheelHistory
import ui.app.assign.detail.wheel.preview.pages.AppWheelItems
import ui.app.assign.detail.wheel.preview.pages.AppWheelPreview
import ui.app.assign.typeName
import ui.widget.*

@ComposableNavNode(AppDetailArgs::class)
@Composable
fun AppPreviewWheelUI() {
    with(AppPreviewWheelScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun AppPreviewWheelScope.Content(nnvl: AppPreviewWheelScope.Wrapper) {
    val deleteDialogState = DialogState.remember()

    AppDeleteDialog(deleteDialogState, AppType.WHEEL) { deleteLogo: Boolean, deleteContent: Boolean ->
        action(AppPreviewWheelVM.Action.Delete(deleteLogo, deleteContent))
    }

    val toolbarActions = remember {
        buildList {
            add(
                ToolbarAction(
                    icon = Svg.Download,
                    visible = vm.showDownloadButton,
                    tooltip = Res.string.toolbar_action_csv_export
                ) {
                    action(AppPreviewWheelVM.Action.ExportHistory)
                })
            if (nnvl.canDelete) {
                add(ToolbarAction.delete {
                    deleteDialogState.show()
                })
            }
            if (nnvl.canEdit) {
                if (nnvl.data.archived) {
                    add(ToolbarAction(Svg.Package_up, tooltip = Res.string.layer_bottom_detail__action_move_from_archive_to_active) {
                        action(AppPreviewWheelVM.Action.Archive(false))
                    })
                } else {
                    add(ToolbarAction(Svg.Package_down, tooltip = Res.string.layer_bottom_detail__action_move_to_archive) {
                        action(AppPreviewWheelVM.Action.Archive(true))
                    })
                }
                add(ToolbarAction.edit {
                    nav.navTo_AppEditorWheelUI(args)
                })
            }
        }
    }
    Column(Modifier.fillMaxSize()) {
        Toolbar(
            nav = nav,
            titlePrefix = AppType.WHEEL.typeName().rememberString(),
            titleSuffix = "(${nnvl.data.id})",
            actions = toolbarActions
        )
        if (display.isMobileOrSmall) VariantMobile(nnvl)
        else VariantDesktop(nnvl)
    }
}

private class MobileTabsStruct(
    val label: StringResource,
    val body: @Composable AppPreviewWheelScope.(AppPreviewWheelScope.Wrapper) -> Unit
) {
    companion object {
        val all by lazy {
            listOf(
                MobileTabsStruct(
                    label = Res.string.layer_bottom_wheel_preview_tabs_preview,
                    body = { AppWheelPreview(it, Modifier.fillMaxSize()) }
                ),
                MobileTabsStruct(
                    label = Res.string.layer_bottom_wheel_preview_tabs_items,
                    body = { AppWheelItems(it, Modifier.fillMaxSize()) }
                ),
                MobileTabsStruct(
                    label = Res.string.layer_bottom_wheel_preview_tabs_usage,
                    body = { AppWheelHistory(Modifier.fillMaxSize()) }
                ),
            )
        }
    }
}

@Composable
private fun AppPreviewWheelScope.VariantMobile(nnvl: AppPreviewWheelScope.Wrapper) {
    var activePageIndex by remember {
        mutableStateOf(0)
    }
    Column(Modifier.fillMaxSize()) {
        Crossfade(
            targetState = activePageIndex,
            modifier = Modifier.fillMaxWidth()
                .weight(1F)
        ) {
            MobileTabsStruct.all[it].body(this@VariantMobile, nnvl)
        }
        TabRow(
            selectedTabIndex = activePageIndex,
            modifier = Modifier.fillMaxWidth()
        ) {
            MobileTabsStruct.all.forEachIndexed { index, page ->
                Tab(
                    selected = index == activePageIndex,
                    onClick = { activePageIndex = index },
                    text = { Text(text = page.label.rememberString()) })
            }
        }
    }
}

@Composable
private fun AppPreviewWheelScope.VariantDesktop(nnvl: AppPreviewWheelScope.Wrapper) {
    Row(
        modifier = Modifier.fillMaxSize(),
        horizontalArrangement = Arrangement.spacedBy(2.dp)
    ) {
        MobileTabsStruct.all.forEach {
            Column(Modifier.weight(1F).fillMaxHeight()) {
                Text(
                    text = it.label.rememberString(),
                    modifier = Modifier.align(Alignment.CenterHorizontally)
                        .padding(8.dp),
                    style = MaterialTheme.typography.headlineSmall
                )
                Box(Modifier.fillMaxSize().background(MaterialTheme.colorScheme.surfaceContainer)) {
                    it.body(this@VariantDesktop, nnvl)
                }
            }
        }
    }
}